import { useEffect, useState } from "react"
import { useTranslations, useBootstrap } from "../../lib/translations"
import { api } from "../../lib/api"

// GithubPill — minimal "Connect GitHub" entry point in the header.
//
// - If the current Company has an active installation: pill shows
//   "GitHub: <login>" (read-only chip — clicking goes to the connected
//   account's install URL would be a future expansion).
// - If not, and the signed-in user has owner/admin role in the current
//   company: pill shows "Connect GitHub". Click → POST to
//   /api/v1/github_installations/start → window.location to GitHub
//   install URL with a signed state token. GitHub redirects back to
//   /webhooks/github/install which creates the Company::GithubInstallation
//   row and bounces back to the dashboard.
// - If the user can't install (member without owner/admin) and there's
//   no installation: hidden (don't tease an action they can't take).
//
// Async-progress pattern (per app/javascript/crew_magic/CLAUDE.md +
// STYLE_GUIDE.md): the click shows "Connecting to GitHub…" until the
// browser navigation completes; if the start API fails, shows an error
// message inline and resets.
export function GithubPill() {
  const t = useTranslations()
  const bootstrap = useBootstrap()
  const [state, setState] = useState({ loading: true, installations: [], error: null, connecting: false })

  const currentCompanyId = bootstrap.current_company?.id
  const roles = (bootstrap.memberships || [])
    .find((m) => m.company_id === currentCompanyId)?.roles || []
  const canInstall = roles.includes("owner") || roles.includes("admin")

  useEffect(() => {
    let cancelled = false
    api
      .get(bootstrap.paths.github_installations)
      .then((data) => {
        if (cancelled) return
        setState((s) => ({ ...s, loading: false, installations: data || [] }))
      })
      .catch(() => {
        if (cancelled) return
        setState((s) => ({ ...s, loading: false, installations: [] }))
      })
    return () => { cancelled = true }
  }, [bootstrap.paths.github_installations])

  if (state.loading) return null

  const active = state.installations.find((i) => !i.suspended_at)

  if (active) {
    return (
      <div className="cm-iconbtn" title={t("shell.github.connected", { login: active.github_account_login })}
           style={{ width: "auto", padding: "0 10px", gap: 6, display: "inline-flex", alignItems: "center" }}>
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
          <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.92.58.11.79-.25.79-.55 0-.27-.01-.99-.02-1.95-3.2.7-3.88-1.54-3.88-1.54-.52-1.33-1.27-1.68-1.27-1.68-1.04-.71.08-.7.08-.7 1.15.08 1.75 1.18 1.75 1.18 1.02 1.75 2.68 1.24 3.34.95.1-.74.4-1.24.72-1.53-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.18-3.1-.12-.29-.51-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11.06 11.06 0 015.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.24 2.76.12 3.05.74.81 1.18 1.84 1.18 3.1 0 4.42-2.69 5.4-5.25 5.69.41.36.78 1.07.78 2.16 0 1.56-.01 2.82-.01 3.21 0 .31.21.67.8.55 4.56-1.53 7.85-5.84 7.85-10.91C23.5 5.65 18.35.5 12 .5z" />
        </svg>
        <span style={{ fontSize: 12 }}>{t("shell.github.connected", { login: active.github_account_login })}</span>
      </div>
    )
  }

  if (!canInstall) return null

  const connect = async () => {
    setState((s) => ({ ...s, connecting: true, error: null }))
    try {
      const res = await api.post(bootstrap.paths.github_installations_start)
      if (res && res.install_url) {
        window.location.href = res.install_url
      } else {
        setState((s) => ({ ...s, connecting: false, error: t("shell.github.connect_failed") }))
      }
    } catch (e) {
      setState((s) => ({ ...s, connecting: false, error: t("shell.github.connect_failed") }))
    }
  }

  return (
    <div className="cm-iconbtn" onClick={state.connecting ? undefined : connect}
         title={state.error || t("shell.github.connect")}
         style={{ width: "auto", padding: "0 10px", gap: 6, display: "inline-flex", alignItems: "center",
                  cursor: state.connecting ? "default" : "pointer", opacity: state.connecting ? 0.7 : 1 }}>
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
        <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.92.58.11.79-.25.79-.55 0-.27-.01-.99-.02-1.95-3.2.7-3.88-1.54-3.88-1.54-.52-1.33-1.27-1.68-1.27-1.68-1.04-.71.08-.7.08-.7 1.15.08 1.75 1.18 1.75 1.18 1.02 1.75 2.68 1.24 3.34.95.1-.74.4-1.24.72-1.53-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.18-3.1-.12-.29-.51-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11.06 11.06 0 015.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.24 2.76.12 3.05.74.81 1.18 1.84 1.18 3.1 0 4.42-2.69 5.4-5.25 5.69.41.36.78 1.07.78 2.16 0 1.56-.01 2.82-.01 3.21 0 .31.21.67.8.55 4.56-1.53 7.85-5.84 7.85-10.91C23.5 5.65 18.35.5 12 .5z" />
      </svg>
      <span style={{ fontSize: 12 }}>
        {state.connecting ? t("shell.github.connecting") : t("shell.github.connect")}
      </span>
    </div>
  )
}
